import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';
import { reject } from 'lodash/collection';
import moment from 'moment-timezone';

import PatientReimbursementApi from '../../../../../../api/patient/PatientReimbursementApi';
import ModalBoxes from '../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import { dateTime12 } from '../../../../../../constants/dateFormat';
import { toBill } from '../../../../../../services/financial';
import { onRequestError } from '../../../../../../services/handlers';
import { NS_COMMON, NSC_REIMBURSEMENT, NSPP_REIMBURSEMENT } from '../../../../../../services/i18n/namespaces';
import { reimbursementItemTypesMap } from '../EncountersSection/PaymentsSection/reimbursementConstants';

export default function useGridData(ssuPatientId, reimbursementId, editItemAllowed) {
  const { t } = useTranslation();
  const [gridData, setGridData] = useState([]);

  const onDelete = useCallback(
    function(itemId) {
      ModalBoxes.confirm({
        title: t('confirmation'),
        content: t(`${NSPP_REIMBURSEMENT}:request.deleteItemConfirmation`),
        cancelButton: t('cancel'),
        confirmButton: t('confirm')
      }).then(
        () => {
          PatientReimbursementApi.deleteReimbursementItem(ssuPatientId, reimbursementId, itemId).then(function({
            data
          }) {
            setGridData(function(items) {
              return reject(items, ['id', itemId]);
            });
          },
          onRequestError);
        },
        () => {}
      );
    },
    [reimbursementId, ssuPatientId, t]
  );

  const columns = useMemo(
    () => [
      {
        field: 'updatedAt',
        headerName: t(`${NSPP_REIMBURSEMENT}:table.uploadDate`),
        flex: 1,
        valueGetter(value) {
          return new Date(value);
        },
        renderCell({ value }) {
          return value ? moment(value).format(dateTime12) : '—';
        },
        type: 'date'
      },
      {
        field: 'type',
        headerName: t(`${NS_COMMON}:type`),
        flex: 1,
        renderCell({ value }) {
          return value ? t(`${NSC_REIMBURSEMENT}:types.${reimbursementItemTypesMap[value]}`) : '—';
        }
      },
      {
        field: 'amount',
        headerName: t(`${NS_COMMON}:amount`),
        flex: 1,
        renderCell({ value }) {
          return value ? `$${toBill(value)}` : '—';
        }
      },
      {
        field: 'files',
        headerName: t(`${NSPP_REIMBURSEMENT}:table.attachments`),
        flex: 1,
        valueGetter(value) {
          return value?.length || 0;
        }
      },
      {
        field: 'note',
        headerName: t(`${NSPP_REIMBURSEMENT}:table.notes`),
        flex: 1
      },
      {
        field: 'actions',
        headerName: t(`${NSPP_REIMBURSEMENT}:table.actions`),
        flex: 1,
        renderCell({ row }) {
          return (
            <IconButton
              disabled={!editItemAllowed}
              aria-label="delete"
              onClick={function(e) {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                onDelete(row.id);
              }}
            >
              <DeleteIcon />
            </IconButton>
          );
        }
      }
    ],
    [onDelete, editItemAllowed, t]
  );
  return [columns, gridData, setGridData];
}
